import { Fragment } from 'react';
import { Box, Card, Group, Text, UnstyledButton } from '@mantine/core';
import { Check } from 'react-feather';
import {
  POST_SESSION_CARD_RADIUS,
  POST_SESSION_CARD_SHADOW,
} from '../../../components/session/postSessionCard';

export type PostSessionStep = 'feedback' | 'notes' | 'commitment' | 'done';

interface Props {
  /** Active step index — drives the highlighted bubble. */
  activeStep: number;
  /** Step completion booleans, in order. */
  feedbackComplete: boolean;
  notesComplete: boolean;
  commitmentComplete: boolean;
  /** AC11 gate — hide the Finalize Commitment step when tracking is off. */
  showCommitmentStep: boolean;
  /** AC14 — true once all applicable steps are done. */
  isDone: boolean;
  /** Allows the participant to jump between steps in any order (AC2). */
  onStepClick: (step: PostSessionStep) => void;
}

/**
 * Top-of-page progress stepper for the participant post-session page.
 *
 * MED-1041 — restyled to the Figma "PROGRESS STRIP" (node 2578:13402): a
 * full-width white card holding a left-aligned, compact row of numbered
 * bubbles + labels joined by short connector lines. The ACTIVE step is a dark
 * navy bubble (#1B2B3A) with a white number + bold dark label; UPCOMING steps
 * are light-gray bubbles with gray numbers/labels.
 *
 * Completed steps keep the prior green-check treatment (AC3 — the Figma only
 * shows the landing/active state, so completed / Done-fill aren't redesigned
 * here). Behavior is unchanged (AC2): any-order navigation via `onStepClick`,
 * the `showCommitmentStep` gate, completed-step collapse (owned by the page),
 * the Done bubble filling once everything is complete, and wrap-on-narrow.
 */

const NAVY = '#1B2B3A'; // active bubble + active/complete label
const UPCOMING_BG = '#F3F4F6'; // upcoming bubble fill
const UPCOMING_FG = '#9CA3AF'; // upcoming number + label
const DIVIDER = '#E5E7EB'; // connector line
const COMPLETE_BG = 'var(--mantine-color-medleyGreen-6)'; // completed bubble

export function PostSessionStepper({
  activeStep,
  feedbackComplete,
  notesComplete,
  commitmentComplete,
  showCommitmentStep,
  isDone,
  onStepClick,
}: Props) {
  const steps: { key: PostSessionStep; label: string; complete: boolean }[] = [
    { key: 'feedback', label: 'Leave Feedback', complete: feedbackComplete },
    { key: 'notes', label: 'Review Session Notes', complete: notesComplete },
    ...(showCommitmentStep
      ? [
          {
            key: 'commitment' as const,
            label: 'Finalize Commitment',
            complete: commitmentComplete,
          },
        ]
      : []),
    { key: 'done', label: 'Done', complete: isDone },
  ];

  return (
    <Card
      data-testid="post-session-stepper-card"
      padding={0}
      radius={POST_SESSION_CARD_RADIUS}
      style={{
        backgroundColor: '#FFFFFF',
        boxShadow: POST_SESSION_CARD_SHADOW,
        border: 'none',
      }}
    >
      <Group
        gap={20}
        align="center"
        wrap="wrap"
        style={{ padding: '16px 20px' }}
      >
        {steps.map((step, idx) => {
          const isActive = idx === activeStep && !step.complete;
          const bubbleBg = step.complete
            ? COMPLETE_BG
            : isActive
              ? NAVY
              : UPCOMING_BG;
          const numberColor = isActive ? '#FFFFFF' : UPCOMING_FG;
          const labelColor = step.complete || isActive ? NAVY : UPCOMING_FG;
          const labelWeight = isActive ? 700 : step.complete ? 600 : 500;
          return (
            <Fragment key={step.key}>
              {idx > 0 && (
                <Box
                  aria-hidden="true"
                  style={{
                    width: 32,
                    height: 2,
                    backgroundColor: DIVIDER,
                    flexShrink: 0,
                  }}
                />
              )}
              <UnstyledButton
                onClick={() => onStepClick(step.key)}
                data-testid={`stepper-step-${step.key}`}
                data-completed={step.complete ? 'true' : 'false'}
                data-active={isActive ? 'true' : 'false'}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  flexShrink: 0,
                }}
              >
                <Box
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    backgroundColor: bubbleBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: '#FFFFFF',
                  }}
                >
                  {step.complete ? (
                    <Check size={14} />
                  ) : (
                    <Text
                      component="span"
                      fz={11}
                      fw={700}
                      lh={1}
                      c={numberColor}
                    >
                      {idx + 1}
                    </Text>
                  )}
                </Box>
                <Text
                  fz={13}
                  fw={labelWeight}
                  c={labelColor}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {step.label}
                </Text>
              </UnstyledButton>
            </Fragment>
          );
        })}
      </Group>
    </Card>
  );
}
